import { type ReactNode } from 'react';
import { useContentScanners } from '../../client/content-scanners';
import { renderContentScannerAnchor } from './markdown/content-scanner-dispatch';

interface ScannedUrlProps {
  /** The URL to render. */
  url: string;
  /** Visible label. Defaults to the URL itself. */
  children?: ReactNode;
  /** Class applied to the plain-link fallback only. */
  className?: string;
}

/**
 * Render a URL we already have in hand through the content-scanner registry.
 *
 * `MarkdownContent` swaps a linked URL for its registered `url` scanner while
 * rendering agent prose. This does the same for a URL a surface extracted
 * itself — the pull-request link a `gh pr create` printed into a tool's output,
 * where there is no markdown to walk — so the badge still comes from the
 * plugin that owns the URL, without that surface importing the plugin. The
 * path sibling is {@link ./ScannedPath}.
 *
 * Scanners resolve in registration order, so a specific matcher (the PR badge)
 * wins over a catch-all (the link unfurler). Callers should still pass only
 * URLs they meant to promote: an arbitrary link handed here gets whatever
 * catch-all is registered.
 *
 * Degrades to a plain external link when nothing claims the URL.
 */
export function ScannedUrl({ url, children, className }: ScannedUrlProps) {
  // Subscribe so a late-registering plugin scanner re-renders this in place.
  useContentScanners();
  return (
    <>
      {renderContentScannerAnchor(url, children ?? url, () => (
        <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
          {children ?? url}
        </a>
      ))}
    </>
  );
}
